"use client";

import { Button } from "@repo/ui/components/ui/button";
import { CheckCircle, Coins, SealCheck, Truck } from "@repo/ui/components/icons";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { safeNextPath } from "../../../lib/next-path";
import { VerificationForm } from "../../app/_components/VerificationForm";
import { BrandLogo } from "../../components/BrandLogo";
import { SiteVersion } from "../../components/SiteVersion";
import { BackButton, LoginScreen } from "./LoginScreen";

// /signin as an app screen (PWA, phone, desktop): a back button and the
// brand on top, one task in the middle, the main action in thumb reach.
// Desktop adds a calm brand panel beside the form. New accounts get a
// welcome step with optional identity verification (it's required only at
// checkout, F13).

const FEATURES = [
  { icon: Coins, title: "قیمت لحظه‌ای", text: "پایش لحظه‌ای قیمت‌ها، سهولت در خرید." },
  { icon: SealCheck, title: "تضمین اصالت شمش", text: "تأمین‌کنندگان شمشاپ، برندهای معتبر و قدیمی در بازار ایران هستند." },
  { icon: Truck, title: "ارسال مطمئن به تمام نقاط ایران، و امکان تحویل حضوری", text: "به نشانی شما، یا از انبار با کد تحویل." },
];

export function SignInForm() {
  const router = useRouter();
  const [stage, setStage] = useState<"login" | "welcome" | "verify">("login");
  const next = () => safeNextPath(new URLSearchParams(window.location.search).get("next"));
  const done = () => router.replace(next());

  return (
    <div className="flex min-h-svh">
      <div className="flex w-full flex-col px-5 pt-[calc(env(safe-area-inset-top)+1rem)] pb-[calc(env(safe-area-inset-bottom)+1.25rem)] sm:px-8 lg:w-1/2 lg:px-16">
        <div className="flex items-center">
          <BackButton onClick={() => (window.history.length > 1 ? router.back() : router.push("/"))} />
        </div>

        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col pt-8 sm:justify-center sm:pt-0 sm:pb-16">
          {stage === "login" ? (
            <>
              <Link href="/" aria-label="صفحه اصلی شمشاپ" className="bg-background mb-6 flex h-16 w-16 items-center justify-center rounded-3xl shadow-sm lg:hidden">
                <BrandLogo className="h-10" />
              </Link>
              {/* New accounts: welcome, then Shahkar. Existing accounts still short of level one: straight to Shahkar (skippable). */}
              <LoginScreen onLoggedIn={(user, { isNewUser }) => (isNewUser ? setStage("welcome") : user.verificationStatus !== "verified" ? setStage("verify") : done())} />
            </>
          ) : stage === "welcome" ? (
            <div className="flex flex-1 flex-col sm:flex-none">
              <span className="bg-tint-green text-ink-green mb-6 flex h-16 w-16 items-center justify-center rounded-3xl">
                <CheckCircle size={32} />
              </span>
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-display">خوش آمدید</h1>
              <p className="text-muted-foreground mt-2 text-sm leading-6">
                حساب شمشاپ شما ساخته شد. برای خرید، یک‌بار هویت خود را با کد ملی تایید کنید؛ همین حالا یا هنگام خرید.
              </p>
              <div className="mt-auto flex flex-col gap-3 pt-8 sm:mt-8">
                <Button type="button" size="lg" onClick={() => setStage("verify")} className="text-base">
                  تایید هویت
                </Button>
                <button type="button" onClick={done} className="text-muted-foreground hover:text-foreground h-11 text-sm">
                  بعداً
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 flex-col sm:flex-none">
              <h1 className="text-2xl font-extrabold tracking-tight sm:text-display">تایید هویت</h1>
              <p className="text-muted-foreground mt-2 mb-6 text-sm leading-6">
                کد ملی صاحب همین شماره موبایل را وارد کنید؛ با سامانه شاهکار بررسی می‌شود.
              </p>
              <VerificationForm embedded onVerified={done} />
              <button type="button" onClick={done} className="text-muted-foreground hover:text-foreground mt-4 h-11 text-sm">
                بعداً
              </button>
            </div>
          )}
        </div>
        {/* The storefront's version (F173), centred at the bottom. */}
        <SiteVersion className="text-muted-foreground mt-6 text-center text-xs" />
      </div>

      <aside className="hidden p-4 lg:flex lg:w-1/2">
        <div className="bg-tint-yellow flex flex-1 flex-col justify-between gap-12 rounded-[32px] p-12 xl:p-16">
          <Link href="/" aria-label="صفحه اصلی شمشاپ" className="bg-background/80 flex h-16 w-16 items-center justify-center rounded-3xl">
            <BrandLogo className="h-10" />
          </Link>
          <div>
            <h2 className="text-ink-yellow text-sm font-semibold">شمشاپ</h2>
            <p className="mt-3 max-w-md text-4xl leading-[1.35] font-extrabold tracking-tight">خرید آنلاین شمش طلا و نقره، ساده و امن</p>
            <ul className="mt-10 flex max-w-md flex-col gap-5">
              {FEATURES.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex items-start gap-4">
                  <span className="bg-background/80 text-ink-yellow flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl">
                    <Icon size={22} />
                  </span>
                  <span>
                    <span className="block font-semibold">{title}</span>
                    <span className="text-foreground/70 text-sm leading-6">{text}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
